import { useState, useEffect } from 'react'
import { useTaskStore } from '@/store/useTaskStore'
import { History, ExternalLink, CheckCircle, XCircle, Clock, ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { api } from '@/lib/api'

interface TaskRun {
  id: string
  task_id: string
  status: string
  started_at: string
  completed_at: string | null
  total_discovered: number | null
  final_count: number | null
  duration_minutes: number | null
  started_by_name: string
}

export function TaskHistory() {
  const selectedTaskId = useTaskStore((s) => s.selectedTaskId)
  const [runs, setRuns] = useState<TaskRun[]>([])
  const [loading, setLoading] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    if (selectedTaskId) {
      loadHistory()
    }
  }, [selectedTaskId])

  const loadHistory = async () => {
    if (!selectedTaskId) return

    try {
      setLoading(true)
      const data = await api.getTaskHistory(selectedTaskId)
      setRuns(data.runs || [])
    } catch (error) {
      console.error('Load history error:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'done':
        return <CheckCircle className="w-4 h-4 text-green-400" />
      case 'error':
        return <XCircle className="w-4 h-4 text-red-400" />
      case 'running':
        return <Clock className="w-4 h-4 text-blue-400 animate-pulse" />
      default:
        return <Clock className="w-4 h-4 text-slate-400" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done':
        return 'bg-green-500/20 text-green-400'
      case 'error':
        return 'bg-red-500/20 text-red-400'
      case 'running':
        return 'bg-blue-500/20 text-blue-400'
      default:
        return 'bg-slate-500/20 text-slate-400'
    }
  }

  const viewResults = async (runId: string) => {
    try {
      await api.downloadExportXlsx(runId)
    } catch (error) {
      console.error('Export XLSX failed:', error)
    }
  }

  if (!selectedTaskId) {
    return null
  }

  return (
    <Card>
      <CardHeader
        className="cursor-pointer select-none hover:bg-slate-800/30 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
          )}
          <History className="w-4 h-4 text-orange-400" />
          <CardTitle>Task History</CardTitle>
          {!isExpanded && runs.length > 0 && (
            <span className="ml-auto text-xs text-slate-500">{runs.length} runs</span>
          )}
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent>
          <div className="max-h-96 overflow-y-auto -mx-6">
        {loading ? (
          <div className="p-6 text-center text-slate-400">Loading history...</div>
        ) : runs.length === 0 ? (
          <div className="p-6 text-center text-slate-400">No runs yet</div>
        ) : (
          <div className="divide-y divide-slate-700">
            {runs.map((run) => (
              <div key={run.id} className="p-4 hover:bg-slate-800/30">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusIcon(run.status)}
                      <span className={`text-xs px-2 py-1 rounded ${getStatusColor(run.status)}`}>
                        {run.status}
                      </span>
                      <span className="text-xs text-slate-400">
                        by {run.started_by_name}
                      </span>
                    </div>

                    <div className="text-sm text-slate-400 space-y-1">
                      <div>Started: {new Date(run.started_at).toLocaleString()}</div>
                      {run.completed_at && (
                        <div>Completed: {new Date(run.completed_at).toLocaleString()}</div>
                      )}
                      {run.duration_minutes && (
                        <div>Duration: {Math.round(run.duration_minutes)} minutes</div>
                      )}
                      {run.total_discovered !== null && (
                        <div className="text-white">
                          Found: {run.total_discovered} profiles → {run.final_count} qualified
                        </div>
                      )}
                    </div>
                  </div>

                  {run.status === 'done' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => viewResults(run.id)}
                      className="gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
        </CardContent>
      )}
    </Card>
  )
}
